import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useShareStore, global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { apiFetch } from "@shared/api/api";
import {
  DEFAULT_TENANT_ID,
  fetchClientIp,
  fetchTenantName,
  hydrateSession,
} from "@shared/api/sessionHydrate";
import { SharePayload } from "@shared/type/ShareType";
import { ApiError } from "@shared/type/ApiType";

const ShareLanding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPendingPayload } = useShareStore();
  const { userInfo } = global();
  // 세션 부트스트랩으로 스토어가 채워지면 userInfo 의존성 때문에 effect가 다시 돈다.
  // 공유 payload를 두 번 읽지 않도록 진입당 1회만 실행한다.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const rawSearch = location.search || window.location.search || "";
    const searchParams = new URLSearchParams(rawSearch);

    // 공유 링크에 sso_token이 붙어 있으면 BE SSO 엔드포인트로 "최상위 이동"한다.
    // fetch(redirect:"manual")로는 same-origin 302가 opaque(status 0)로 내려와
    // Location도, BE가 알려주는 테넌트도 읽을 수 없다. 세션 쿠키는 이 이동에서 심어지고,
    // BE는 r로 넘긴 경로에 tenant를 붙여 되돌려준다.
    const ssoToken = searchParams.get("sso_token");
    if (ssoToken) {
      searchParams.delete("sso_token");
      const restSearch = searchParams.toString();
      const returnPath =
        window.location.pathname +
        (restSearch ? `?${restSearch}` : "") +
        window.location.hash;

      window.location.replace(
        `/snotes/share/sso/telegram?token=${encodeURIComponent(
          ssoToken
        )}&r=${encodeURIComponent(returnPath)}`
      );
      return;
    }

    const load = async () => {
      try {
        // 로그인 전/후 복귀 처리용으로 공유 토큰 보관
        sessionStorage.setItem("share_link_token", token);

        // SSO 복귀 시 BE가 세션을 등록한 테넌트를 쿼리로 알려준다.
        // 텔레그램에서 처음 들어온 브라우저는 스토어가 비어 있어 이 값이 유일한 단서다.
        const tenantFromSso = searchParams.get("tenant");
        const tenantId =
          tenantFromSso || userInfo?.tenant_id || DEFAULT_TENANT_ID;

        // 사용자 정보가 없으면(SSO 직후 등) 세션 기준으로 스토어를 채운다.
        // 다른 사용자로 로그인돼 있던 브라우저일 수 있어 SSO 복귀 시에는 항상 갱신한다.
        if (tenantFromSso || !userInfo?.cd_user) {
          const [dcIp, tenantName] = await Promise.all([
            fetchClientIp(),
            fetchTenantName(tenantId),
          ]);
          await hydrateSession({ tenantId, tenantName, dcIp });
        }

        const res = await apiFetch<{ payload: SharePayload }>(
          `/api/share/${token}`,
          {
            headers: {
              "X-TENANT-ID": tenantId,
            },
          }
        );
        const payload = res.data?.payload;
        if (!payload) {
          throw new Error("empty payload");
        }
        setPendingPayload(payload);

        // 세션이 유효하면 payload를 전역에 저장하고 메인으로 이동한다
        sessionStorage.removeItem("share_link_token");
        navigate("/MainLayout", { replace: true });
      } catch (error: any) {
        let msg = "유효하지 않거나 만료된 공유 링크입니다.";
        if (error instanceof ApiError) {
          msg = `${msg}\n(code: ${error.code}) ${error.message}`;
        } else if (error?.message) {
          msg = `${msg}\n${error.message}`;
        }
        // 세션 만료/권한 오류는 안내 후 로그인 화면으로 이동
        await showDialog("error", msg);
        sessionStorage.setItem(
          "return_path",
          window.location.pathname +
            window.location.search +
            window.location.hash
        );
        navigate("/login", { replace: true });
      }
    };

    load();
  }, [location.search, navigate, setPendingPayload, token, userInfo?.cd_user]);

  return (
    <div className="w-full h-full flex items-center justify-center">
      공유 화면 준비중...
    </div>
  );
};

export default ShareLanding;
